import { useState } from "react";
import styled from "styled-components";
import CloseIcon from "../icon/closeIcon";
import { isSonEomNeunNal, LUNAR_RANGE } from "../../utils/lunar";

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  z-index: 9998;
`;

const Wrapper = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  width: 96vw;
  max-width: 1480px;
  height: 94vh;
  max-height: 94vh;
  background-color: white;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.18);

  /* 모바일: 화면을 거의 꽉 채워 공간 최대 활용 */
  @media (max-width: 767px) {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const Header = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: clamp(12px, 3vw, 26px) clamp(14px, 4vw, 36px);
  border-bottom: 1px solid #eee;

  /* 모바일: 제목과 컨트롤이 좁은 폭에서도 겹치지 않도록 */
  @media (max-width: 767px) {
    flex-wrap: wrap;
  }
`;

const TitleBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
`;

const Title = styled.div`
  font-size: clamp(19px, 4.8vw, 30px);
  font-weight: 800;
  color: #0f0f0f;
  letter-spacing: 1px;
`;

const SubTitle = styled.div`
  font-size: clamp(11px, 2.6vw, 14px);
  color: #8b8b8b;
  font-weight: 400;
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(8px, 2vw, 16px);
`;

const YearNav = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(8px, 2vw, 14px);
`;

const NavButton = styled.button<{ $disabled?: boolean }>`
  width: clamp(28px, 7vw, 34px);
  height: clamp(28px, 7vw, 34px);
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  background-color: white;
  color: ${(props) => (props.$disabled ? "#cfcfcf" : "#ff7f3b")};
  font-size: clamp(14px, 3.5vw, 18px);
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.15s;
  &:hover {
    background-color: ${(props) => (props.$disabled ? "white" : "#fff4ee")};
  }
`;

const YearLabel = styled.div`
  font-size: clamp(16px, 4vw, 22px);
  font-weight: 800;
  color: #ff7f3b;
  min-width: clamp(64px, 16vw, 88px);
  text-align: center;
`;

const CloseBox = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: clamp(14px, 3vw, 30px);
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: clamp(12px, 2vw, 22px);

  /* 태블릿: 2열 고정으로 카드를 크고 시원하게 */
  @media (min-width: 768px) and (max-width: 1199px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 18px;
  }

  /* 모바일: 1열, 카드 폭 최대 활용 */
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 14px;
    padding: 14px;
  }
`;

const Legend = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: clamp(11px, 2.6vw, 13px);
  color: #555;
  margin-bottom: 2px;
`;

const LegendMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background-color: #ff7f3b;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  color: #ffffff;
`;

const MonthCard = styled.div`
  border: 1px dashed #d9d9d9;
  border-radius: 10px;
  padding: clamp(10px, 1.8vw, 18px);
`;

const MonthTitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: clamp(8px, 1.4vw, 12px);
`;

const MonthKo = styled.span`
  font-size: clamp(16px, 4vw, 20px);
  font-weight: 800;
  color: #2b2b2b;
`;

const MonthEn = styled.span`
  font-size: clamp(10px, 2.4vw, 12px);
  font-weight: 600;
  color: #9a9a9a;
  letter-spacing: 0.5px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 4px;
`;

const WeekHead = styled.div<{ $dow: number }>`
  text-align: center;
  font-size: clamp(10px, 2.6vw, 13px);
  font-weight: 700;
  padding-bottom: 6px;
  color: ${(props) =>
    props.$dow === 0 ? "#e8483b" : props.$dow === 6 ? "#1e73e8" : "#9a9a9a"};

  @media (max-width: 1199px) {
    font-size: clamp(11px, 3vw, 14px);
  }
`;

const DayCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
`;

const DayInner = styled.div<{ $dow: number; $son: boolean; $today: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: clamp(24px, 5.5vw, 32px);
  height: clamp(24px, 5.5vw, 32px);
  font-size: clamp(12px, 3vw, 15px);
  font-weight: ${(props) => (props.$son ? 700 : 400)};
  /* 손없는 날: 흰색 글씨 / 그 외: 일요일 빨강·토요일 파랑·평일 진회색 */
  color: ${(props) =>
    props.$son
      ? "#ffffff"
      : props.$dow === 0
      ? "#e8483b"
      : props.$dow === 6
      ? "#1e73e8"
      : "#3a3a3a"};
  border-radius: 50%;
  /* 손없는 날: 테마 주황 채움 / 오늘(손없는 날 아님): 옅은 주황 링 */
  background-color: ${(props) =>
    props.$son ? "#ff7f3b" : props.$today ? "#fff0e6" : "transparent"};
  border: 1.5px solid
    ${(props) => (!props.$son && props.$today ? "#ffc59e" : "transparent")};

  /* 태블릿·모바일: 셀을 키워 가독성/터치 영역 확보 */
  @media (max-width: 1199px) {
    width: clamp(28px, 7vw, 36px);
    height: clamp(28px, 7vw, 36px);
    font-size: clamp(13px, 3.6vw, 16px);
  }
`;

const MONTH_EN = [
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
];
const WEEK_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

// 한 달의 셀(빈칸 포함) 배열 생성
const buildMonthCells = (year: number, month: number): (number | null)[] => {
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
};

const MonthBlock = ({ year, month }: { year: number; month: number }) => {
  const cells = buildMonthCells(year, month);
  const now = new Date();
  const isCurrentMonth =
    now.getFullYear() === year && now.getMonth() === month;

  return (
    <MonthCard>
      <MonthTitle>
        <MonthKo>{month + 1}월</MonthKo>
        <MonthEn>{MONTH_EN[month]}</MonthEn>
      </MonthTitle>
      <Grid>
        {WEEK_LABELS.map((label, idx) => (
          <WeekHead key={`h-${idx}`} $dow={idx}>
            {label}
          </WeekHead>
        ))}
        {cells.map((day, idx) => {
          if (day === null) return <DayCell key={`e-${idx}`} />;
          const dow = (idx % 7);
          const date = new Date(year, month, day);
          const son = isSonEomNeunNal(date);
          const today = isCurrentMonth && now.getDate() === day;
          return (
            <DayCell key={`d-${idx}`}>
              <DayInner $dow={dow} $son={son} $today={today}>
                {day}
              </DayInner>
            </DayCell>
          );
        })}
      </Grid>
    </MonthCard>
  );
};

const CalendarModal = (props: { onClose: () => void }) => {
  const { onClose } = props;
  const [year, setYear] = useState<number>(new Date().getFullYear());

  const canPrev = year > LUNAR_RANGE.MIN_YEAR;
  const canNext = year < LUNAR_RANGE.MAX_YEAR;

  return (
    <>
      <Backdrop onClick={onClose} />
      <Wrapper>
        <Header>
          <TitleBox>
            <Title>손없는 날</Title>
            <SubTitle>음력 끝자리가 9 또는 0인 날 (이사·개업 길일)</SubTitle>
          </TitleBox>
          <RightControls>
            <YearNav>
              <NavButton
                $disabled={!canPrev}
                onClick={() => canPrev && setYear((y) => y - 1)}
              >
                ◀
              </NavButton>
              <YearLabel>{year}년</YearLabel>
              <NavButton
                $disabled={!canNext}
                onClick={() => canNext && setYear((y) => y + 1)}
              >
                ▶
              </NavButton>
            </YearNav>
            <CloseBox>
              <CloseIcon onClick={onClose} height={"24px"} fill={"#AEAEAE"} />
            </CloseBox>
          </RightControls>
        </Header>
        <Body>
          <Legend>
            <LegendMark>9</LegendMark>
            동그라미 표시된 날이 손없는 날입니다.
          </Legend>
          {Array.from({ length: 12 }, (_, m) => (
            <MonthBlock key={m} year={year} month={m} />
          ))}
        </Body>
      </Wrapper>
    </>
  );
};

export default CalendarModal;
